import { useEffect, useState } from "react"

import { css } from "@emotion/css"
import {
  fetchCampaignMbaConfig,
  patchCampaignMbaConfig,
  patchClientCampaignMbaConfig,
} from "api/mba"
import { Loading } from "components/Loading"
import { Container } from "components/Page"
import { queryCache, useMutation, useQuery } from "react-query"
import { AggregatedFields } from "./AggregatedFields"
import { ExportSeparateSubmedias } from "./ExportSeparateSubmedias"
import { ExportTvView } from "./ExportTvView"
import { ExportBudgetInsights } from "./ExportBudgetInsights"
import { GeneralSettings } from "routes/Base/Admin/MbaConfiguration/GeneralSettings"
import { MediaTypesSettings } from "routes/Base/Admin/MbaConfiguration/MediaTypesSettings"
import { TableColumnHeader } from "./components/TableColumnHeader"
import { TableColumnPicker } from "./components/TableColumnPicker"
import { TableColumnOrder } from "./components/TableColumnOrder"
import { GroupByMediaFields } from "routes/Base/Admin/MbaConfiguration/GroupByMediaFields"
import { MediaTypesFields } from "routes/Base/Admin/MbaConfiguration/MediaTypesFields"
import { useAuthenticated } from "providers/A12n"
import { MbaConfiguration } from "models/mba"
import { ApiError, ErrorsMap } from "api/http/utils"
import { ExportVersionTracker } from "./ExportVersionTracker"
import Button from "ui/buttons/Button"
import { useI18n } from "providers/I18n"

type Props = {
  level: string
  configScope?: "CAMPAIGN" | "CLIENT_AGENCY"
  id?: string
}

export const CampaignMbaConfig = (props: Props) => {
  const { t } = useI18n()
  const { level, configScope, id } = props
  const { hasAuthority } = useAuthenticated()
  const [errors, setErrors] = useState<ErrorsMap | undefined>()
  const [changedData, setChangedData] = useState<MbaConfiguration | undefined>()

  const { data, isLoading } = useQuery(
    [`${configScope}-mba-config`, level, id],
    fetchCampaignMbaConfig,
  )
  useEffect(() => {
    setChangedData({ ...data })
  }, [data])

  const handleError = (e: Error) => {
    const error = e as ApiError
    setErrors(error?.errorsMap)
  }

  const [updateCampaignLevel, { isLoading: isLoadingCampaignLevelUpdate }] =
    useMutation(patchCampaignMbaConfig, {
      onSuccess: () => {
        queryCache.invalidateQueries([`${configScope}-mba-config`, level, id])
      },
      onError: handleError,
    })

  const [updateClientLevel, { isLoading: isLoadingClientLevelUpdate }] =
    useMutation(patchClientCampaignMbaConfig, {
      onSuccess: () => {
        queryCache.invalidateQueries([`${configScope}-mba-config`, level, id])
      },
      onError: handleError,
    })

  const update =
    configScope === "CAMPAIGN" ? updateCampaignLevel : updateClientLevel

  const isAllowed = hasAuthority(["AUTH_EDIT_MBA_CONFIG"])

  const updateChangedData = (data: MbaConfiguration) =>
    setChangedData((prev) => ({ ...prev, ...data }))

  return isLoading || Object.keys(data ?? {}).length === 0 ? (
    <Loading />
  ) : (
    <Container className={styles.container} key={id}>
      <GeneralSettings
        data={changedData?.general}
        update={(data) => {
          setChangedData((prev) => ({
            ...prev,
            ...data,
            general: { ...prev?.general, ...data.general },
          }))
        }}
        id={id}
        viewIntermediary={changedData?.viewIntermediary}
        disabled={!isAllowed}
        configScope={configScope}
      />
      <MediaTypesSettings
        data={changedData?.mediaTypes}
        update={updateChangedData}
        id={id}
        configScope={configScope}
      />
      <GroupByMediaFields
        data={changedData?.mediaTypes}
        update={updateChangedData}
        id={id}
        identifier="semanticCode"
        disabled={!isAllowed}
        headerComponent={TableColumnHeader}
        tablePickerComponent={TableColumnPicker}
        tableOrderComponent={TableColumnOrder}
        configScope={configScope}
      />
      <MediaTypesFields
        data={changedData?.mediaTypes}
        update={updateChangedData}
        id={id}
        disabled={!isAllowed}
        identifier="semanticCode"
        headerComponent={TableColumnHeader}
        tablePickerComponent={TableColumnPicker}
        tableOrderComponent={TableColumnOrder}
        configScope={configScope}
      />
      <AggregatedFields
        data={changedData?.mediaTypes}
        update={updateChangedData}
        id={id}
        disabled={!isAllowed}
        configScope={configScope}
      />
      <ExportVersionTracker
        data={changedData}
        id={id}
        disabled={!isAllowed}
        update={updateChangedData}
        configScope={configScope}
      />
      <ExportSeparateSubmedias
        data={changedData}
        update={updateChangedData}
        id={id}
        disabled={!isAllowed}
        configScope={configScope}
      />
      <ExportTvView
        data={changedData}
        update={updateChangedData}
        id={id}
        disabled={!isAllowed}
        configScope={configScope}
      />
      <ExportBudgetInsights
        data={changedData}
        update={updateChangedData}
        id={id}
        disabled={!isAllowed}
        configScope={configScope}
      />
      <div className={styles.sticky}>
        <Button
          variant="default"
          color="primary"
          onClick={() => setChangedData(data)}
          disabled={
            isLoadingCampaignLevelUpdate ||
            isLoadingClientLevelUpdate ||
            isLoading
          }
        >
          {t("mba.discard.changes")}
        </Button>
        <Button
          disabled={
            isLoadingCampaignLevelUpdate ||
            isLoadingClientLevelUpdate ||
            isLoading
          }
          variant="contained"
          color="primary"
          onClick={() => update({ ...changedData, id } as MbaConfiguration)}
        >
          {t("mba.save.changes")}
        </Button>
      </div>
      {errors}
    </Container>
  )
}

const styles = {
  container: css`
    padding: var(--spacing-xxl-2) 0px 0px;
    display: flex;
    min-width: 100%;
    flex-direction: column;
    gap: var(--spacing-xxl-6);
  `,
  sticky: css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80px;
    position: sticky;
    bottom: 0;
    background-color: var(--white);
    gap: var(--spacing-s);
  `,
}
